/**
 * @typedef {Object} InventoryResponseDto
 * @property {number} inventoryId
 * @property {number} componentId
 * @property {string} componentName
 * @property {string} componentCode
 * @property {number} warehouseId
 * @property {string} warehouseName
 * @property {number} [locationId]
 * @property {number} quantityOnHand
 * @property {number} quantityReserved
 * @property {number} quantityAvailable
 * @property {number[]} [zoneIds]
 * @property {string[]} [zoneNames]
 */

/**
 * @typedef {Object} InventoryAdjustDto
 * @property {number} componentId
 * @property {number} warehouseId
 * @property {number} [locationId]
 * @property {number} quantityDelta
 * @property {boolean} [isProcessed]
 * @property {string} [notes]
 * @property {number[]} [zoneIds] - Replaces the stock row's zone tag set (optional, multi-valued). Omit/null to leave untouched, [] to clear.
 */

/**
 * @typedef {Object} InventoryPlacementUpdateDto
 * @property {number} [locationId] - Null clears the location.
 * @property {number[]} [zoneIds] - Full replacement of the zone tag set. Null/omit clears all tags.
 */

/**
 * @typedef {Object} InventoryTransferDto
 * @property {number} componentId
 * @property {number} fromWarehouseId
 * @property {number} [fromLocationId]
 * @property {number} toWarehouseId
 * @property {number} [toLocationId]
 * @property {number} quantity
 * @property {boolean} [isProcessed]
 * @property {string} [notes]
 */

/**
 * @typedef {Object} InventoryConsignmentResponseDto
 * @property {number} consignmentId
 * @property {number} componentId
 * @property {string} componentName
 * @property {string} componentCode
 * @property {number} warehouseId
 * @property {string} warehouseName
 * @property {number} unitCost
 * @property {number} receivedQuantity
 * @property {number} remainingQuantity
 * @property {string} receivedDate
 * @property {string} sourceType
 * @property {number} [sourceId]
 * @property {boolean} isBackorder
 * @property {boolean} isSettled
 */

/**
 * @typedef {Object} ConsignmentTransactionResponseDto
 * @property {number} consignmentTransactionId
 * @property {number} consignmentId
 * @property {string} transactionTypeCode
 * @property {number} quantity
 * @property {number} unitCost
 * @property {string} referenceType
 * @property {number} [referenceId]
 * @property {number} [relatedConsignmentId]
 * @property {string} [notes]
 * @property {string} createdDate
 * @property {string} createdBy
 */

const BASE = '/v1/inventory';

export default (instance) => ({
  getTransactionTypes: () =>
    instance.get(`${BASE}/transaction-types`),

  getAll: (params = {}) =>
    instance.get(BASE, { params }),

  adjust: (payload) =>
    instance.post(`${BASE}/adjust`, payload),

  transfer: (payload) =>
    instance.post(`${BASE}/transfer`, payload),

  updatePlacement: (inventoryId, payload) =>
    instance.put(`${BASE}/${inventoryId}/placement`, payload),

  getConsignments: (params = {}) =>
    instance.get(`${BASE}/consignments`, { params }),

  getConsignmentTransactions: (consignmentId) =>
    instance.get(`${BASE}/consignments/${consignmentId}/transactions`),

  getTransactions: (params = {}) =>
    instance.get(`${BASE}/transactions`, { params }),

  getAllocationsByOrderLine: (orderLineId) =>
    instance.get(`${BASE}/allocations/by-order-line/${orderLineId}`),

  createAllocation: (payload) =>
    instance.post(`${BASE}/allocations`, payload),

  issueAllocation: (allocationId, payload) =>
    instance.post(`${BASE}/allocations/${allocationId}/issue`, payload)
});
